import { create } from 'zustand';
import { useAuthStore } from '@/store/authStore';

/** 서버가 만료 시간을 안 알려 줄 때 쓰는 해제 시간 */
export const PRIVACY_REVEAL_MS = 60_000;

interface PrivacyState {
  /** 해제가 끝나는 시각(ms). null 이면 가린 상태 */
  revealedUntil: number | null;
  /**
   * 서버가 원본을 보내게 하는 해제 토큰. api.ts 가 `X-Pii-Reveal` 헤더로 싣는다.
   * 서버는 이 토큰이 없으면 이름·연락처·이메일을 가린 채 보낸다.
   */
  revealToken: string | null;
  reveal: (revealToken: string, expiresInMs?: number) => void;
  conceal: () => void;
}

let expiryTimer: number | undefined;

/**
 * 개인정보 가림 해제 상태. 화면 하나가 아니라 **앱 전체**에 걸린다 — 기관 관리에서
 * 풀고 사용자 관리로 넘어갔을 때 또 비밀번호를 묻는 것은 1분짜리 해제에 비해
 * 번거롭기만 하다.
 *
 * 일부러 저장(persist)하지 않는다. 새로고침하면 다시 가려지는 편이 안전하다.
 */
export const usePrivacyStore = create<PrivacyState>()((set) => ({
  revealedUntil: null,
  revealToken: null,
  reveal: (revealToken, expiresInMs = PRIVACY_REVEAL_MS) => {
    window.clearTimeout(expiryTimer);
    expiryTimer = window.setTimeout(
      () => set({ revealedUntil: null, revealToken: null }),
      expiresInMs,
    );
    set({ revealedUntil: Date.now() + expiresInMs, revealToken });
  },
  conceal: () => {
    window.clearTimeout(expiryTimer);
    set({ revealedUntil: null, revealToken: null });
  },
}));

// 로그아웃하면 남은 시간과 상관없이 바로 가린다 — 다음 사람이 같은 브라우저로 들어올 수 있다
useAuthStore.subscribe((state, prev) => {
  if (prev.isAuthenticated && !state.isAuthenticated) usePrivacyStore.getState().conceal();
});
